import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';
import {
  Close,
  Phone,
  Email,
  LocationOn,
  Edit,
  Cake,
  Save,
  Cancel,
  PhotoCamera,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { User } from '../../types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import authService from '../../services/auth';
import { uploadAvatar } from '../../api/userApi';
import { useAppDispatch } from '../../hooks/redux';
import { updateUserProfile } from '../../store/actions/user.actions';
import { getAvatarColors } from '../../utils/avatarColors';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  currentUser: User | null;
  onEdit?: (user: User) => void;
  onContact?: (member: User, contactType: 'phone' | 'email') => void;
  onViewLocation?: (member: User) => void;
  onUpdate?: (updatedUser: User) => void;
  onOpenChat?: (member: User) => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  user,
  currentUser,
  onUpdate,
  onEdit,
  onContact,
  onViewLocation,
  onOpenChat
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isIsolated, setIsIsolated] = useState(false);
  const [isolationSaving, setIsolationSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    occupation: '',
    bio: '',
    avatarColor: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    birthday: '',
    isLocationVisible: false,
    isBirthdayVisible: false
  });

  // Move useEffect before early return
  React.useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        displayName: user.displayName || user.display_name || '',
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
        phoneNumber: user.phoneNumber || user.phone_number || '',
        occupation: user.occupation || '',
        bio: user.bio || '',
        avatarColor: user.avatar_color || '',
        address1: user.address?.address1 || '',
        address2: user.address?.address2 || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        country: user.address?.country || '',
        postalCode: user.address?.postalCode || '',
        birthday: convertBirthdayForInput(user.birthday),
        isLocationVisible: Boolean(user.isLocationVisible ?? user.is_location_visible ?? false),
        isBirthdayVisible: Boolean(user.isBirthdayVisible ?? user.is_birthday_visible ?? false)
      });
    }
  }, [user]);

  // Sync isolation state when user prop changes (must be before early return)
  React.useEffect(() => {
    setIsIsolated(!!(user as any)?.is_isolated);
  }, [user]);

  // Debug logging (moved before early return)
  React.useEffect(() => {
    if (currentUser && user) {
      console.log('ProfileModal Debug:', {
        currentUserId: currentUser.id,
        currentUserIdType: typeof currentUser.id,
        targetUserId: user.id,
        targetUserIdType: typeof user.id,
        canEdit: currentUser && (
          currentUser.id === user.id ||
          String(currentUser.id) === '1' ||
          String(currentUser.id) === '2'
        ),
        isAdmin: String(currentUser.id) === '1',
        isManager: String(currentUser.id) === '2'
      });
    }
  }, [currentUser, user]);

  if (!user) return null;

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const convertBirthdayForInput = (birthday: string | undefined | null): string => {
    if (!birthday) return '';

    // If it's already in YYYY-MM-DD format, return as is
    if (birthday.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return birthday;
    }

    // If it's in ISO format, extract the date part
    if (birthday.includes('T')) {
      return birthday.split('T')[0];
    }

    // For any other format, we need the original date value
    // This should ideally come from the raw backend data before formatting
    return '';
  };

  const formatBirthday = (birthday: string | null) => {
    if (!birthday) return null;
    try {
      // Handle different possible formats
      if (typeof birthday === 'string') {
        // Check if it's in ISO format (YYYY-MM-DDTHH:MM:SS.sssZ) or simple YYYY-MM-DD format
        if (birthday.match(/^\d{4}-\d{2}-\d{2}/) && birthday.includes('T')) {
          // ISO format - extract just the date part
          const datePart = birthday.split('T')[0];
          const parts = datePart.split('-');
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Month is 0-indexed
          const day = parseInt(parts[2]);

          // Validate the parsed values
          if (isNaN(year) || isNaN(month) || isNaN(day) || year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
            return 'Invalid Date';
          }

          return new Date(year, month, day).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        } else if (birthday.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Simple YYYY-MM-DD format
          const parts = birthday.split('-');
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Month is 0-indexed
          const day = parseInt(parts[2]);

          // Validate the parsed values
          if (isNaN(year) || isNaN(month) || isNaN(day) || year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
            return 'Invalid Date';
          }

          // Create date in local timezone
          const date = new Date(year, month, day);

          // Check if the date is valid
          if (isNaN(date.getTime())) {
            return 'Invalid Date';
          }

          return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          });
        }

        // Fallback for other formats
        const date = new Date(birthday + 'T00:00:00');
        return date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      }

      return null;
    } catch (error) {
      console.error('Error formatting birthday:', error);
      return birthday;
    }
  };

  const getLocationText = (member: User) => {
    if (member.address && (member.address.city || member.address.state)) {
      return `${member.address.city || ''}${member.address.city && member.address.state ? ', ' : ''}${member.address.state || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
    }
    if (member.location && (member.isLocationVisible || member.is_location_visible)) {
      return 'Live location available';
    }
    return 'No location shared';
  };

  const canEdit = currentUser && (
    currentUser.id === user.id ||
    String(currentUser.id) === '1' ||  // admin
    String(currentUser.id) === '2'     // manager
  );

  const isAdminOrManager = currentUser && (String(currentUser.id) === '1' || String(currentUser.id) === '2');
  const isOtherUser = currentUser && String(currentUser.id) !== String(user.id);
  const canIsolate = isAdminOrManager && isOtherUser && !['1','2'].includes(String(user.id));

  const handleToggleIsolation = async () => {
    if (!canIsolate) return;
    const next = !isIsolated;
    setIsolationSaving(true);
    try {
      const res = await fetch(`/api/auth/admin/user/${user.id}/isolate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isolated: next }),
      });
      const data = await res.json();
      if (data.success) setIsIsolated(next);
    } catch {}
    setIsolationSaving(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!currentUser || !user) return;

    // Check if email has changed
    const emailChanged = formData.email !== user.email;

    setIsSaving(true);
    try {
      // Prepare data for API
      const updateData = {
        email: formData.email,
        displayName: formData.displayName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        occupation: formData.occupation,
        bio: formData.bio,
        avatar_color: formData.avatarColor,
        birthday: formData.birthday,
        isLocationVisible: formData.isLocationVisible,
        isBirthdayVisible: formData.isBirthdayVisible,
        address: {
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          postalCode: formData.postalCode
        }
      };

      console.log('ProfileModal: Saving with avatar_color:', formData.avatarColor, 'Full updateData:', updateData);

      // Use admin endpoint if current user is admin/manager and editing someone else
      const isAdminEdit = Number(currentUser.id) !== Number(user.id) && (Number(currentUser.id) === 1 || Number(currentUser.id) === 2);

      console.log('ProfileModal Save - isAdminEdit check:', {
        currentUserId: currentUser.id,
        targetUserId: user.id,
        isAdminEdit,
        currentUserIdType: typeof currentUser.id,
        targetUserIdType: typeof user.id
      });

      let updatedUser;
      if (isAdminEdit) {
        // Use admin endpoint
        console.log(`ProfileModal: Using admin endpoint for user ${user.id}`);
        const response = await fetch(`/api/auth/admin/user/${user.id}/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          console.error('Admin endpoint error:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('Admin endpoint response:', errorText);
          throw new Error(`Failed to update profile: ${response.status} ${response.statusText}`);
        }
        const result = await response.json();
        console.log('ProfileModal: Admin endpoint response:', result);
        updatedUser = result.data;
        console.log('ProfileModal: Updated user from admin endpoint:', updatedUser);
      } else {
        // Use Redux action like the working UserProfile component
        const profileData = {
          email: updateData.email,
          display_name: updateData.displayName,
          first_name: updateData.firstName,
          last_name: updateData.lastName,
          phone_number: updateData.phoneNumber,
          occupation: updateData.occupation,
          bio: updateData.bio,
          avatar_color: updateData.avatar_color,
          address: updateData.address,
          birthday: updateData.birthday,
          isLocationVisible: updateData.isLocationVisible,
          isBirthdayVisible: updateData.isBirthdayVisible
        };

        await dispatch(updateUserProfile(profileData));
        // Get the updated user from Redux store - this should happen automatically
        // Update the user object with the new data for immediate UI update
        updatedUser = {
          ...user,
          email: updateData.email,
          display_name: updateData.displayName,
          displayName: updateData.displayName,
          first_name: updateData.firstName,
          firstName: updateData.firstName,
          last_name: updateData.lastName,
          lastName: updateData.lastName,
          phone_number: updateData.phoneNumber,
          phoneNumber: updateData.phoneNumber,
          occupation: updateData.occupation,
          bio: updateData.bio,
          avatar_color: updateData.avatar_color,
          birthday: updateData.birthday,
          isLocationVisible: updateData.isLocationVisible,
          is_location_visible: updateData.isLocationVisible,
          isBirthdayVisible: updateData.isBirthdayVisible,
          is_birthday_visible: updateData.isBirthdayVisible,
          address: updateData.address
        };
      }

      // Update the parent component with the new user data
      if (onUpdate && updatedUser) {
        onUpdate(updatedUser);
      }

      // If email changed and user is editing their own profile, force logout
      if (emailChanged && Number(currentUser.id) === Number(user.id)) {
        alert('Your email address has been updated. You will now be logged out. Please log in with your new email address.');

        // Logout and redirect
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include'
        });

        // Clear Redux store and redirect to login
        window.location.href = '/auth';
        return;
      }

      // Exit edit mode but keep modal open so user sees the updated avatar color
      setIsEditing(false);
      setSaveSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    if (canEdit) {
      setIsEditing(true);
    } else if (onEdit) {
      onEdit(user);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setLocalAvatarUrl(null);
    setSaveSuccess(false);
    // Reset form data to original values
    if (user) {
      setFormData({
        email: user.email || '',
        displayName: user.displayName || user.display_name || '',
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
        phoneNumber: user.phoneNumber || user.phone_number || '',
        occupation: user.occupation || '',
        bio: user.bio || '',
        avatarColor: user.avatar_color || '',
        address1: user.address?.address1 || '',
        address2: user.address?.address2 || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        country: user.address?.country || '',
        postalCode: user.address?.postalCode || '',
        birthday: convertBirthdayForInput(user.birthday),
        isLocationVisible: Boolean(user.isLocationVisible ?? user.is_location_visible ?? false),
        isBirthdayVisible: Boolean(user.isBirthdayVisible ?? user.is_birthday_visible ?? false)
      });
    }
  };

  const handleChangePassword = async () => {
    if (pwNew !== pwConfirm) { setPwMessage({ type: 'error', text: 'New passwords do not match' }); return; }
    if (pwNew.length < 8) { setPwMessage({ type: 'error', text: 'New password must be at least 8 characters' }); return; }
    setPwSaving(true);
    setPwMessage(null);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew }),
      });
      const data = await res.json();
      if (data.success) {
        setPwMessage({ type: 'success', text: 'Password changed successfully' });
        setPwCurrent(''); setPwNew(''); setPwConfirm('');
        setTimeout(() => { setShowPasswordForm(false); setPwMessage(null); }, 2000);
      } else {
        setPwMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch {
      setPwMessage({ type: 'error', text: 'Network error — please try again' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleAvatarClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setUploadingAvatar(true);
    try {
      const response = await uploadAvatar(file);
      if (response.data?.success && response.data.data?.avatarUrl) {
        const newAvatarUrl = response.data.data.avatarUrl;
        setLocalAvatarUrl(newAvatarUrl);

        // Update parent component
        if (onUpdate && user) {
          onUpdate({ ...user, photo_url: newAvatarUrl, photoURL: newAvatarUrl });
        }
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to delete your avatar?')) return;

    try {
      const profileData = {
        photo_url: ''
      };
      await dispatch(updateUserProfile(profileData));
      setLocalAvatarUrl(null);

      // Update parent component
      if (onUpdate && user) {
        onUpdate({ ...user, photo_url: '', photoURL: '' });
      }
    } catch (error) {
      console.error('Failed to delete avatar:', error);
      alert('Failed to delete avatar. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <span style={{ fontSize: '1.25rem', fontWeight: 500 }}>Profile Details</span>
        <IconButton onClick={onClose}>
          <Close sx={{ fontSize: 28 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {saveSuccess && (
          <Alert icon={<CheckCircle />} severity="success" sx={{ mb: 2 }}>
            Profile updated successfully!
          </Alert>
        )}
        {isEditing ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Box sx={{ position: 'relative' }}>
                  {(() => {
                    const name = user.displayName || user.display_name || user.firstName || user.first_name || '';
                    const avatarColors = getAvatarColors(name, formData.avatarColor);
                    return (
                      <Avatar
                        src={localAvatarUrl || user.photoURL || user.photo_url}
                        sx={{
                          width: 120,
                          height: 120,
                          mb: 2,
                          boxShadow: 3,
                          cursor: 'pointer',
                          backgroundColor: avatarColors.backgroundColor,
                          color: avatarColors.color,
                          fontSize: '3rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={handleAvatarClick}
                      >
                        {name ? name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    );
                  })()}
                  <IconButton
                    size="small"
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }}
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                  {(localAvatarUrl || user.photoURL || user.photo_url) && (
                    <IconButton
                      size="small"
                      onClick={handleDeleteAvatar}
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {uploadingAvatar ? 'Uploading...' : 'Click to change avatar'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Avatar Color:</Typography>
                  <input
                    type="color"
                    value={formData.avatarColor || getAvatarColors(user.displayName || user.display_name || user.firstName || user.first_name || '', null).backgroundColor}
                    onChange={(e) => handleInputChange('avatarColor', e.target.value)}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '2px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  {formData.avatarColor && (
                    <Button
                      size="small"
                      onClick={() => handleInputChange('avatarColor', '')}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Reset to Auto
                    </Button>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {formData.avatarColor ? 'Custom' : 'Auto'}
                  </Typography>
                </Box>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </Box>
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                margin="normal"
                helperText="Changing your email will log you out. You'll need to log in with the new email."
              />
              <TextField
                fullWidth
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Occupation"
                value={formData.occupation}
                onChange={(e) => handleInputChange('occupation', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Birthday"
                type="date"
                value={formData.birthday}
                onChange={(e) => handleInputChange('birthday', e.target.value)}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={3}
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Address</Typography>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.address1}
                onChange={(e) => handleInputChange('address1', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.address2}
                onChange={(e) => handleInputChange('address2', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                margin="normal"
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Privacy Settings</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isLocationVisible}
                      onChange={(e) => handleInputChange('isLocationVisible', e.target.checked)}
                    />
                  }
                  label="Location Visible"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isBirthdayVisible}
                      onChange={(e) => handleInputChange('isBirthdayVisible', e.target.checked)}
                    />
                  }
                  label="Birthday Visible"
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                {(() => {
                  const name = user.displayName || user.display_name || user.firstName || user.first_name || '';
                  const avatarColors = getAvatarColors(name, user.avatar_color);
                  return (
                    <Box sx={{ position: 'relative', width: 150, height: 150, mb: 2 }}>
                      <Avatar
                        src={user.photoURL || user.photo_url}
                        sx={{
                          width: 150,
                          height: 150,
                          position: 'absolute',
                          backgroundColor: (user.photoURL || user.photo_url) ? 'transparent' : avatarColors.backgroundColor,
                          color: avatarColors.color,
                          fontSize: '4rem',
                          fontWeight: 'bold',
                          clipPath: 'circle(50%)',
                          '& img': {
                            clipPath: 'circle(50%)'
                          }
                        }}
                      >
                        {name ? name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 150,
                          height: 150,
                          borderRadius: '50%',
                          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 -1px 2px rgba(255, 255, 255, 0.1)',
                          pointerEvents: 'none',
                          zIndex: 1
                        }}
                      />
                    </Box>
                  );
                })()}

                <Typography variant="h5" align="center" gutterBottom>
                  {user.displayName || user.display_name || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || 'Unknown User'}
                </Typography>

                {user.occupation && (
                  <Typography variant="subtitle1" color="textSecondary" align="center">
                    {user.occupation}
                  </Typography>
                )}

                {user.id === currentUser?.id && (
                  <Chip label="You" color="primary" size="small" sx={{ mt: 1 }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {user.id !== currentUser?.id && onOpenChat && (
                  <Button
                    variant="contained"
                    startIcon={<span className="material-icons">chat_bubble</span>}
                    onClick={() => onOpenChat(user)}
                    sx={{
                      mb: 1,
                      width: '100%',
                      bgcolor: 'var(--primary-color)',
                      '&:hover': { bgcolor: 'var(--primary-dark)' }
                    }}
                  >
                    Send Message
                  </Button>
                )}

                <IconButton
                  onClick={() => onContact?.(user, 'phone')}
                  disabled={!(user.phoneNumber || user.phone_number)}
                  color="primary"
                  size="large"
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <Phone />
                </IconButton>

                <IconButton
                  onClick={() => onContact?.(user, 'email')}
                  color="primary"
                  size="large"
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <Email />
                </IconButton>

                <IconButton
                  onClick={() => onViewLocation?.(user)}
                  disabled={!(user.isLocationVisible || user.is_location_visible) && !user.address}
                  color="primary"
                  size="large"
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <LocationOn />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box sx={{ height: '100%' }}>
                {user.bio && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>About</Typography>
                    <Typography variant="body1" sx={{ fontStyle: 'italic', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      "{user.bio}"
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <Box sx={{ pl: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <Email sx={{ mr: 2, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}
                      >
                        {user.email}
                      </Typography>
                    </Box>

                    {(user.phoneNumber || user.phone_number) && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {formatPhoneNumber(user.phoneNumber || user.phone_number || '')}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <LocationOn sx={{ mr: 2, color: 'text.secondary' }} />
                      <Typography variant="body1">{getLocationText(user)}</Typography>
                    </Box>

                    {user.birthday && (user.isBirthdayVisible || user.is_birthday_visible) && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <Cake sx={{ mr: 2, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {formatBirthday(user.birthday)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {user.address && (user.address.address1 || user.address.city || user.address.state) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Address</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body1">
                        {[
                          user.address.address1,
                          user.address.address2,
                          [user.address.city, user.address.state].filter(Boolean).join(', '),
                          user.address.postalCode
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        {isEditing ? (
          <>
            <Button
              onClick={handleCancelEdit}
              variant="outlined"
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<Save />}
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            {canIsolate && (
              <Button
                onClick={handleToggleIsolation}
                disabled={isolationSaving}
                variant="outlined"
                color={isIsolated ? 'warning' : 'inherit'}
                startIcon={<span className="material-icons" style={{ fontSize: 18 }}>{isIsolated ? 'lock_open' : 'lock'}</span>}
                title={isIsolated ? 'Remove communication restriction' : 'Restrict to admin-only communication'}
              >
                {isIsolated ? 'Unrestrict' : 'Restrict'}
              </Button>
            )}
            {currentUser && user && String(currentUser.id) === String(user.id) && (
              <Button
                onClick={() => { setShowPasswordForm(v => !v); setPwMessage(null); }}
                variant="outlined"
                startIcon={<span className="material-icons" style={{ fontSize: 18 }}>lock</span>}
              >
                {showPasswordForm ? 'Cancel' : 'Change Password'}
              </Button>
            )}
            {canEdit && (
              <Button
                onClick={handleEdit}
                variant="contained"
                startIcon={<Edit />}
                color="primary"
              >
                Edit Profile
              </Button>
            )}
          </>
        )}
      </DialogActions>

      {/* Inline change-password form — only visible when toggled by the profile owner */}
      {showPasswordForm && !isEditing && currentUser && user && String(currentUser.id) === String(user.id) && (
        <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pwMessage && (
            <Alert severity={pwMessage.type}>{pwMessage.text}</Alert>
          )}
          <TextField
            label="Current Password"
            type="password"
            size="small"
            fullWidth
            value={pwCurrent}
            onChange={e => setPwCurrent(e.target.value)}
            disabled={pwSaving}
          />
          <TextField
            label="New Password"
            type="password"
            size="small"
            fullWidth
            value={pwNew}
            onChange={e => setPwNew(e.target.value)}
            disabled={pwSaving}
            helperText="Minimum 8 characters"
          />
          <TextField
            label="Confirm New Password"
            type="password"
            size="small"
            fullWidth
            value={pwConfirm}
            onChange={e => setPwConfirm(e.target.value)}
            disabled={pwSaving}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
            onClick={handleChangePassword}
            startIcon={<span className="material-icons" style={{ fontSize: 18 }}>{pwSaving ? 'hourglass_empty' : 'check'}</span>}
          >
            {pwSaving ? 'Saving...' : 'Update Password'}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};